// API Functions für Supabase-Interaktionen

import { supabase } from './supabase';
import type {
  Lead,
  Customer,
  LeadFilters,
  PaginationParams,
  PaginatedResponse,
  LeadUpdateData,
  CustomerFormData,
  DashboardStats,
  CustomerStats,
  Project,
  ProjectFormData,
  FormSubmission,
  FormType,
  SubmissionStatus,
} from './types';

// ===========================================
// LEADS
// ===========================================

/**
 * Leads abrufen mit Filtern und Pagination
 */
export async function getLeads(
  filters?: LeadFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<Lead>> {
  let query = supabase
    .from('leads')
    .select(`
      *,
      customer:customers (
        id,
        name,
        slug
      ),
      project:projects (
        id,
        name,
        customer_id,
        customer:customers (
          id,
          name,
          slug
        )
      )
    `, { count: 'exact' });

  // Filter anwenden
  if (filters?.customer_slug) {
    query = query.eq('customer_slug', filters.customer_slug);
  }

  if (filters?.project_id) {
    query = query.eq('project_id', filters.project_id);
  }

  if (filters?.form_identifier) {
    query = query.eq('form_identifier', filters.form_identifier);
  }

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  if (filters?.date_from) {
    query = query.gte('created_at', filters.date_from);
  }

  if (filters?.date_to) {
    query = query.lte('created_at', filters.date_to);
  }

  // Freitext-Suche (durchsucht Name, E-Mail und fields)
  if (filters?.search) {
    // Suche in contact_name und contact_email
    query = query.or(`contact_name.ilike.%${filters.search}%,contact_email.ilike.%${filters.search}%`);
  }

  // Sortierung
  query = query.order('created_at', { ascending: false });

  // Pagination
  const page = pagination?.page || 1;
  const perPage = pagination?.per_page || 25;
  const from = (page - 1) * perPage;
  const to = from + perPage - 1;

  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Fehler beim Laden der Leads: ${error.message}`);
  }

  return {
    data: data as Lead[] || [],
    total: count || 0,
    page,
    per_page: perPage,
    total_pages: Math.ceil((count || 0) / perPage),
  };
}

/**
 * Einzelnen Lead abrufen
 */
export async function getLead(id: string): Promise<Lead> {
  const { data, error } = await supabase
    .from('leads')
    .select(`
      *,
      customer:customers (*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Fehler beim Laden des Leads: ${error.message}`);
  }

  return data as Lead;
}

/**
 * Lead aktualisieren
 */
export async function updateLead(id: string, updates: LeadUpdateData): Promise<Lead> {
  const { data, error } = await supabase
    .from('leads')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(`Fehler beim Aktualisieren des Leads: ${error.message}`);
  }

  return data as Lead;
}

/**
 * „Kein Spam": vom Spamfilter abgefangenen Lead zurückholen und nachträglich
 * an den Betrieb weiterleiten. Die Datenbank prüft die Berechtigung (Admin oder
 * der Betrieb, dem der Lead gehört) — siehe lead_kein_spam in Migration 094.
 */
export async function leadKeinSpam(id: string): Promise<'weitergeleitet' | 'bereits_weitergeleitet' | 'testlauf'> {
  const { data, error } = await supabase.rpc('lead_kein_spam', { p_lead: id });

  if (error) {
    throw new Error(`Lead konnte nicht zurückgeholt werden: ${error.message}`);
  }

  return data as 'weitergeleitet' | 'bereits_weitergeleitet' | 'testlauf';
}

/**
 * Lead löschen
 */
export async function deleteLead(id: string): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .delete()
    .eq('id', id);

  if (error) {
    throw new Error(`Fehler beim Löschen des Leads: ${error.message}`);
  }
}

/**
 * Mehrere Leads aktualisieren (Bulk Update)
 */
export async function updateLeadsBulk(ids: string[], updates: LeadUpdateData): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .update(updates)
    .in('id', ids);

  if (error) {
    throw new Error(`Fehler beim Bulk-Update: ${error.message}`);
  }
}

/**
 * Alle Formular-Identifiers abrufen (für Filter)
 */
export async function getFormIdentifiers(customerSlug?: string): Promise<string[]> {
  let query = supabase
    .from('leads')
    .select('form_identifier')
    .order('form_identifier');

  if (customerSlug) {
    query = query.eq('customer_slug', customerSlug);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Fehler beim Laden der Formular-Typen: ${error.message}`);
  }

  // Unique values
  const uniqueIdentifiers = [...new Set(data.map(item => item.form_identifier))];
  return uniqueIdentifiers;
}

/**
 * Anzahl der Anfragen pro Formular-Typ
 */
export async function getFormCounts(customerSlug?: string): Promise<Record<string, number>> {
  let query = supabase
    .from('leads')
    .select('form_identifier');

  if (customerSlug) {
    query = query.eq('customer_slug', customerSlug);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Fehler beim Laden der Formular-Statistiken: ${error.message}`);
  }

  // Zähle pro form_identifier
  const counts: Record<string, number> = {};
  data.forEach(item => {
    const form = item.form_identifier;
    counts[form] = (counts[form] || 0) + 1;
  });

  return counts;
}

// ===========================================
// CUSTOMERS
// ===========================================

/**
 * Alle Kunden abrufen
 */
export async function getCustomers(): Promise<Customer[]> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .order('name');

  if (error) {
    throw new Error(`Fehler beim Laden der Kunden: ${error.message}`);
  }

  return data as Customer[];
}

/**
 * Einzelnen Kunden abrufen
 */
export async function getCustomer(id: string): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Fehler beim Laden des Kunden: ${error.message}`);
  }

  return data as Customer;
}

/**
 * Kunden erstellen
 */
export async function createCustomer(customerData: CustomerFormData): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .insert(customerData)
    .select()
    .single();

  if (error) {
    throw new Error(`Fehler beim Erstellen des Kunden: ${error.message}`);
  }

  return data as Customer;
}

/**
 * Kunden aktualisieren
 */
export async function updateCustomer(id: string, customerData: Partial<CustomerFormData>): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .update(customerData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(`Fehler beim Aktualisieren des Kunden: ${error.message}`);
  }

  return data as Customer;
}

/**
 * Kunden löschen (inkl. aller verknüpften Projekte und Leads)
 */
export async function deleteCustomer(id: string): Promise<void> {
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', id);

  if (error) {
    throw new Error(`Fehler beim Löschen des Kunden: ${error.message}`);
  }
}

// ===========================================
// PROJECTS (Formulare/Landingpages pro Kunde)
// ===========================================

/**
 * Alle Projekte abrufen (optional: nur für einen Kunden)
 */
export async function getProjects(customerId?: string): Promise<Project[]> {
  let query = supabase
    .from('projects')
    .select(`
      *,
      customer:customers (id, name, slug)
    `)
    .order('name');

  if (customerId) {
    query = query.eq('customer_id', customerId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Fehler beim Laden der Projekte: ${error.message}`);
  }

  return data as Project[];
}

/**
 * Einzelnes Projekt abrufen
 */
export async function getProject(id: string): Promise<Project> {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      *,
      customer:customers (*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Fehler beim Laden des Projekts: ${error.message}`);
  }

  return data as Project;
}

/**
 * Projekt erstellen
 */
export async function createProject(projectData: ProjectFormData): Promise<Project> {
  const { data, error } = await supabase
    .from('projects')
    .insert(projectData)
    .select()
    .single();

  if (error) {
    throw new Error(`Fehler beim Erstellen des Projekts: ${error.message}`);
  }

  return data as Project;
}

/**
 * Projekt aktualisieren
 */
export async function updateProject(id: string, projectData: Partial<ProjectFormData>): Promise<Project> {
  const { data, error } = await supabase
    .from('projects')
    .update(projectData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(`Fehler beim Aktualisieren des Projekts: ${error.message}`);
  }

  return data as Project;
}

/**
 * Projekt löschen
 */
export async function deleteProject(id: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', id);

  if (error) {
    throw new Error(`Fehler beim Löschen des Projekts: ${error.message}`);
  }
}

// ===========================================
// DASHBOARD STATISTICS
// ===========================================

/**
 * Dashboard-Gesamtstatistiken (optional: nur für einen Kunden)
 */
export async function getDashboardStats(customerSlug?: string): Promise<DashboardStats> {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  
  // Start der Woche (Montag)
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() + diff);
  weekStart.setHours(0, 0, 0, 0);
  const weekStartISO = weekStart.toISOString();

  // Base queries (mit optionalem Kunden-Filter)
  const baseQuery = () => {
    let q = supabase.from('leads').select('id', { count: 'exact', head: true });
    if (customerSlug) q = q.eq('customer_slug', customerSlug);
    return q;
  };

  // Parallel queries
  const [totalResult, newResult, todayResult, weekResult] = await Promise.all([
    baseQuery(),
    baseQuery().eq('status', 'new'),
    baseQuery().gte('created_at', todayStart),
    baseQuery().gte('created_at', weekStartISO),
  ]);

  return {
    total_leads: totalResult.count || 0,
    new_leads: newResult.count || 0,
    today_leads: todayResult.count || 0,
    this_week_leads: weekResult.count || 0,
  };
}

/**
 * Statistiken pro Kunde
 */
export async function getCustomerStats(): Promise<CustomerStats[]> {
  const { data: customers, error: customersError } = await supabase
    .from('customers')
    .select('id, name, slug')
    .eq('active', true)
    .order('name');

  if (customersError) {
    throw new Error(`Fehler beim Laden der Kunden: ${customersError.message}`);
  }

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() + diff);
  weekStart.setHours(0, 0, 0, 0);
  const weekStartISO = weekStart.toISOString();

  const stats: CustomerStats[] = [];

  for (const customer of customers) {
    const [totalResult, newResult, todayResult, weekResult] = await Promise.all([
      supabase.from('leads').select('id', { count: 'exact', head: true }).eq('customer_id', customer.id),
      supabase.from('leads').select('id', { count: 'exact', head: true }).eq('customer_id', customer.id).eq('status', 'new'),
      supabase.from('leads').select('id', { count: 'exact', head: true }).eq('customer_id', customer.id).gte('created_at', todayStart),
      supabase.from('leads').select('id', { count: 'exact', head: true }).eq('customer_id', customer.id).gte('created_at', weekStartISO),
    ]);

    stats.push({
      customer_id: customer.id,
      customer_name: customer.name,
      customer_slug: customer.slug,
      total_leads: totalResult.count || 0,
      new_leads: newResult.count || 0,
      today_leads: todayResult.count || 0,
      this_week_leads: weekResult.count || 0,
    });
  }

  return stats;
}

// ===========================================
// CUSTOMER INVITATIONS
// ===========================================

/**
 * Kunden ins Dashboard einladen
 */
export async function inviteCustomer(customerId: string, email: string, customerName: string): Promise<{ success: boolean; message: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('Nicht eingeloggt');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  
  const response = await fetch(`${supabaseUrl}/functions/v1/invite-customer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      customer_id: customerId,
      email: email,
      customer_name: customerName,
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Fehler beim Einladen');
  }

  return result;
}

// ===========================================
// FORM SUBMISSIONS
// ===========================================

/**
 * Form Submissions abrufen (mit Filtern)
 */
export async function getFormSubmissions(filters?: {
  customer_id?: string;
  project_id?: string;
  form_type?: FormType;
  status?: SubmissionStatus;
  unassigned_only?: boolean;
}): Promise<FormSubmission[]> {
  let query = supabase
    .from('form_submissions_with_details')
    .select('*')
    .order('submitted_at', { ascending: false });

  if (filters?.unassigned_only) {
    query = query.is('customer_id', null);
  } else if (filters?.customer_id) {
    query = query.eq('customer_id', filters.customer_id);
  }

  if (filters?.project_id) {
    query = query.eq('project_id', filters.project_id);
  }

  if (filters?.form_type) {
    query = query.eq('form_type', filters.form_type);
  }

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Fehler beim Laden der Submissions: ${error.message}`);
  }

  return data as FormSubmission[] || [];
}

/**
 * Unzugeordnete Submissions abrufen
 */
export async function getUnassignedSubmissions(): Promise<FormSubmission[]> {
  const { data, error } = await supabase
    .from('unassigned_submissions')
    .select('*');

  if (error) {
    throw new Error(`Fehler beim Laden der unzugeordneten Submissions: ${error.message}`);
  }

  return data as FormSubmission[] || [];
}

/**
 * Einzelne Form Submission abrufen (mit Dateien)
 */
export async function getFormSubmission(id: string): Promise<FormSubmission> {
  const { data: submission, error: submissionError } = await supabase
    .from('form_submissions_with_details')
    .select('*')
    .eq('id', id)
    .single();

  if (submissionError) {
    throw new Error(`Fehler beim Laden der Submission: ${submissionError.message}`);
  }

  // Dateien laden
  const { data: files, error: filesError } = await supabase
    .from('form_submission_files')
    .select('*')
    .eq('submission_id', id);

  if (filesError) {
    console.error('Fehler beim Laden der Dateien:', filesError);
  }

  // Download-URLs generieren
  const filesWithUrls = await Promise.all(
    (files || []).map(async (file) => {
      const { data: urlData } = await supabase.storage
        .from('form-submissions')
        .createSignedUrl(file.storage_path, 3600); // 1 Stunde gültig

      return {
        ...file,
        download_url: urlData?.signedUrl,
      };
    })
  );

  return {
    ...submission,
    files: filesWithUrls,
  } as FormSubmission;
}

/**
 * Form Submission aktualisieren (Status, Notes, Tags)
 */
export async function updateFormSubmission(
  id: string,
  data: {
    status?: SubmissionStatus;
    notes?: string;
    tags?: string[];
    processed_at?: string;
  }
): Promise<FormSubmission> {
  const { data: updated, error } = await supabase
    .from('form_submissions')
    .update(data)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(`Fehler beim Aktualisieren der Submission: ${error.message}`);
  }

  return updated as FormSubmission;
}

/**
 * Form Submission löschen
 */
export async function deleteFormSubmission(id: string): Promise<void> {
  // Erst Dateien aus Storage löschen
  const { data: files } = await supabase
    .from('form_submission_files')
    .select('storage_path')
    .eq('submission_id', id);

  if (files && files.length > 0) {
    const paths = files.map(f => f.storage_path);
    await supabase.storage.from('form-submissions').remove(paths);
  }

  // Dann Submission löschen (Dateien-Einträge werden durch CASCADE gelöscht)
  const { error } = await supabase
    .from('form_submissions')
    .delete()
    .eq('id', id);

  if (error) {
    throw new Error(`Fehler beim Löschen der Submission: ${error.message}`);
  }
}

/**
 * Datei herunterladen
 */
/**
 * Erzeugt eine signierte URL für eine Lead-Datei. Der lead-files-Bucket ist
 * privat – wir signieren beim Zugriff frisch (1h). Zwei Datenformate:
 * Alt-Leads tragen die frühere Public-URL (Pfad steckt hinter dem Marker),
 * neue Landingpages senden nur noch den Storage-Pfad ("<slug>/<datei>").
 */
export async function getLeadFileSignedUrl(fileUrl: string): Promise<string> {
  const marker = '/object/public/lead-files/';
  const idx = fileUrl.indexOf(marker);
  let path: string;
  if (idx !== -1) {
    path = decodeURIComponent(fileUrl.slice(idx + marker.length));
  } else if (!/^https?:\/\//i.test(fileUrl)) {
    path = fileUrl;
  } else {
    // Fremde URL (z.B. schon signiert/anderer Bucket) → unverändert lassen
    return fileUrl;
  }
  const { data, error } = await supabase.storage.from('lead-files').createSignedUrl(path, 3600);
  if (error || !data) throw new Error(error?.message || 'Signierte URL konnte nicht erzeugt werden');
  return data.signedUrl;
}

export async function downloadSubmissionFile(fileId: string): Promise<{ url: string; filename: string }> {
  const { data: file, error: fileError } = await supabase
    .from('form_submission_files')
    .select('*')
    .eq('id', fileId)
    .single();

  if (fileError) {
    throw new Error(`Fehler beim Laden der Datei: ${fileError.message}`);
  }

  const { data: urlData, error: urlError } = await supabase.storage
    .from('form-submissions')
    .createSignedUrl(file.storage_path, 3600);

  if (urlError || !urlData) {
    throw new Error(`Fehler beim Generieren des Download-Links: ${urlError?.message}`);
  }

  return {
    url: urlData.signedUrl,
    filename: file.original_filename,
  };
}

/**
 * Submission einem Kunden zuweisen
 */
export async function assignSubmissionToCustomer(
  submissionId: string,
  customerId: string
): Promise<FormSubmission> {
  const { data, error} = await supabase
    .from('form_submissions')
    .update({ customer_id: customerId })
    .eq('id', submissionId)
    .select()
    .single();

  if (error) {
    throw new Error(`Fehler beim Zuweisen: ${error.message}`);
  }

  return data as FormSubmission;
}

// ===========================================
// CRM & TASK MANAGEMENT
// ===========================================
